<template>
  <Teleport to="body">
    <div
      v-if="isRendered"
      role="dialog"
      class="pdp-bottom-sheet"
      :class="{ 'pdp-bottom-sheet--active': isVisible }"
      aria-modal="true"
      aria-labelledby="bottom-sheet-title-label"
      data-testid="bottom-sheet"
      v-bind="$attrs"
    >
      <button type="button" class="pdp-bottom-sheet__backdrop" aria-label="Close modal" data-testid="bottom-sheet-backdrop" @click="close"></button>

      <div ref="sheetRef" class="pdp-bottom-sheet__container" :style="sheetStyles">
        <div
          v-if="$slots.header || isTitleAvailable"
          class="pdp-bottom-sheet__header"
          data-testid="bottom-sheet-header"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <slot name="header">
            <div class="pdp-bottom-sheet__header-default">
              <span id="bottom-sheet-title-label" class="pdp-bottom-sheet__title large" data-testid="bottom-sheet-title">{{ title }}</span>
              <IconButton :icon="csgClose" size="s" data-testid="bottom-sheet-close-btn" @click="close" />
            </div>
          </slot>
        </div>

        <div ref="contentRef" class="pdp-bottom-sheet__body" data-testid="bottom-sheet-body" @scroll="updateScrollState">
          <slot />
        </div>

        <div v-if="$slots.footer" class="pdp-bottom-sheet__footer" data-testid="bottom-sheet-footer">
          <transition name="sheet-fade">
            <button
              v-if="showScrollArrow"
              type="button"
              class="pdp-bottom-sheet__scroll-btn"
              data-testid="bottom-sheet-scroll-btn"
              @click="scrollNextPage"
            >
              <IconButton :icon="csgChevronSmallDown" size="m" />
            </button>
          </transition>

          <slot name="footer" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed, onBeforeUnmount, nextTick } from 'vue';
import { useScrollLock } from '@vueuse/core';
import { csgClose, csgChevronSmallDown } from '@csgSvgIcons/icons';
import IconButton from '~/components/common/IconButton.vue';

const props = withDefaults(
  defineProps<{
    isOpen: boolean;
    title?: string;
    maxHeight?: string;
    height?: string;
    tabletMaxWidth?: string;
    tabletHeight?: string;
  }>(),
  {
    title: 'Product Details',
    maxHeight: '90dvh',
    height: 'auto',
    tabletMaxWidth: '600px',
    tabletHeight: 'auto',
  },
);

const emit = defineEmits(['close']);

// --- Constants ---
const SCROLL_DISMISS_THRESHOLD = 10;
const SCROLL_BOTTOM_BUFFER = 20;
const GESTURE_CLOSE_THRESHOLD = 100;
const UNMOUNT_DELAY = 400;
const INITIAL_SCROLL_UPDATE_DELAY = 200;

const isRendered = ref(false);
const isVisible = ref(false);
const isTitleAvailable = computed(() => !!props.title);

const isLocked = useScrollLock(computed(() => (typeof document !== 'undefined' ? document.body : null)));

const close = () => {
  if (!isVisible.value) return;
  emit('close');
};

// --- Scroll Logic ---
const contentRef = ref<HTMLElement | null>(null);
const showScrollArrow = ref(false);
const arrowDismissedThisSession = ref(false);

const updateScrollState = () => {
  if (!contentRef.value) return;
  const { scrollTop, scrollHeight, clientHeight } = contentRef.value;

  if (scrollTop > SCROLL_DISMISS_THRESHOLD) {
    arrowDismissedThisSession.value = true;
  }

  if (!arrowDismissedThisSession.value && scrollHeight > clientHeight + scrollTop + SCROLL_BOTTOM_BUFFER) {
    showScrollArrow.value = true;
  } else {
    showScrollArrow.value = false;
  }
};

const scrollNextPage = () => {
  if (!contentRef.value) return;
  contentRef.value.scrollTo({
    top: contentRef.value.scrollHeight,
    behavior: 'smooth',
  });
};

// --- Gesture Logic (Header Only) ---
const startY = ref(0);
const currentY = ref(0);
const isDragging = ref(false);

const sheetStyles = computed(() => {
  const styles: Record<string, string> = {
    '--sheet-max-height': props.maxHeight,
    '--sheet-height': props.height,
    '--sheet-tablet-max-width': props.tabletMaxWidth,
    '--sheet-tablet-height': props.tabletHeight,
  };

  // Only apply inline transform/transition while actively dragging
  // This allows CSS classes to handle the default open/close animations smoothly
  if (isDragging.value) {
    const displacement = Math.max(0, currentY.value - startY.value);
    styles.transform = `translateY(${displacement}px)`;
    styles.transition = 'none';
  }

  return styles;
});

const handleTouchStart = (e: TouchEvent) => {
  startY.value = e?.touches?.[0]?.clientY || 0;
  currentY.value = startY.value;
  isDragging.value = true;
};

const handleTouchMove = (e: TouchEvent) => {
  if (!isDragging.value) return;
  currentY.value = e?.touches?.[0]?.clientY || 0;
};

const handleTouchEnd = () => {
  if (!isDragging.value) return;
  const displacement = currentY.value - startY.value;
  isDragging.value = false;

  if (displacement > GESTURE_CLOSE_THRESHOLD) {
    close();
  }
  startY.value = 0;
  currentY.value = 0;
};

// --- Lifecycle ---
let resizeObserver: ResizeObserver | null = null;

const cleanupResizeObserver = () => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
};

watch(
  () => props.isOpen,
  async (val) => {
    if (val) {
      isRendered.value = true;
      isLocked.value = true;
      arrowDismissedThisSession.value = false;

      // Wait for DOM to render the container
      await nextTick();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!props.isOpen) return; // Prevent race condition if closed before animation
          isVisible.value = true;
          if (contentRef.value) {
            cleanupResizeObserver();
            resizeObserver = new ResizeObserver(updateScrollState);
            resizeObserver.observe(contentRef.value);
          }
          setTimeout(() => {
            if (props.isOpen) updateScrollState();
          }, INITIAL_SCROLL_UPDATE_DELAY);
        });
      });
    } else {
      isVisible.value = false;
      isLocked.value = false;
      cleanupResizeObserver();

      // Wait for animation to finish before unmounting
      setTimeout(() => {
        if (!props.isOpen) {
          isRendered.value = false;
        }
      }, UNMOUNT_DELAY);
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  isLocked.value = false;
  cleanupResizeObserver();
});
</script>

<style lang="scss" scoped>
@import '~settingSCSS';

.pdp-bottom-sheet {
  position: fixed;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  max-width: none;
  max-height: none;
  width: 100%;
  height: 100%;
  overflow: visible;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  pointer-events: none; /* Ignore clicks while animating out */

  &.pdp-bottom-sheet--active {
    pointer-events: auto;
  }
}

.pdp-bottom-sheet__backdrop {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100dvh;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1;
  border: none;
  padding: 0;
  margin: 0;
  opacity: 0;
  transition: opacity 0.4s ease;

  /* iOS Safari: Ensure the backdrop covers the entire visible viewport. */
  @supports (-webkit-touch-callout: none) {
    position: absolute;
  }
}

.pdp-bottom-sheet--active .pdp-bottom-sheet__backdrop {
  opacity: 1;
}

.pdp-bottom-sheet__container {
  position: relative;
  z-index: 2;
  width: 100vw;
  --sheet-bleed: 150px;
  height: calc(var(--sheet-height, 90dvh) + var(--sheet-bleed));
  max-height: calc(var(--sheet-max-height, 90dvh) + var(--sheet-bleed));
  @include ios-bottom-bleed(var(--sheet-bleed));
  background: $white;
  display: flex;
  flex-direction: column;
  transform: translateY(100%);
  transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  border-radius: 10px 10px 0 0;

  @include breakpoint(medium up) {
    width: 100%;
    max-width: var(--sheet-tablet-max-width, 600px);
    height: var(--sheet-tablet-height, 60vh);
    max-height: var(--sheet-max-height);
    margin: 0 auto;
    margin-bottom: 0;
    padding-bottom: 0;
  }
}

.pdp-bottom-sheet--active .pdp-bottom-sheet__container {
  transform: translateY(0);
}

.pdp-bottom-sheet__header {
  flex-shrink: 0;
  touch-action: pan-y;
  user-select: none;
  padding: 14px 16px;
  border-bottom: 1px solid $background-color;
}

.pdp-bottom-sheet__header-default {
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  position: relative;

  .icon-button {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
}

.pdp-bottom-sheet__title {
  text-align: center;
  @include truncate-multiline(1);
  max-width: calc(100% - 64px);
}

.pdp-bottom-sheet__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
}

.pdp-bottom-sheet__footer {
  flex-shrink: 0;
  position: relative;
  @include padding-xs;
  border-top: 1px solid $background-color;
}

.pdp-bottom-sheet__scroll-btn {
  position: absolute;
  @include padding-xxs;
  top: -62px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  background: $white;
  border-radius: 50%;
  box-shadow: $drop-shadow-center;
  animation: bounce-down 2s infinite ease-in-out both;
}

@keyframes bounce-down {
  0%,
  100% {
    transform: translateX(-50%) translateY(0);
  }
  50% {
    transform: translateX(-50%) translateY(6px);
  }
}

/* Animations for the floating arrow */
.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition:
    opacity 0.2s,
    transform 0.2s;
}

.sheet-fade-enter-from,
.sheet-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 10px);
}
</style>
